import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";

export interface User {
  id: string;
  email: string;
  name: string;
  role: "SUPER_ADMIN" | "ADMIN" | "SALES_REP";
  role_label?: string | null;
  company_id?: string; // Optional for SUPER_ADMIN
  avatar?: string;
  profile_picture?: string | null;
  is_online: boolean;
  company?: {
    id: string;
    name: string;
    logo?: string | null;
    logoUrl?: string | null;
  };
  /** True when the user must name their company (OAuth without org). */
  needsCompanySetup?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  paymentRequired: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  paymentRequired: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setPaymentRequired: (state, action: PayloadAction<boolean>) => {
      state.paymentRequired = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.paymentRequired = false;
    },
    updateUserStatus: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.is_online = action.payload;
      }
    },
  },
});

export const { setUser, setLoading, setError, logout, updateUserStatus, setPaymentRequired } =
  authSlice.actions;

export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectPaymentRequired = (state: RootState) => state.auth.paymentRequired;

export default authSlice.reducer;
